// Where an analysis is prepared, step by step: regulatory profile (first time only), type of analysis,
// which pages to cover (or the check scope), then a short summary and «Iniciar análisis». Opening it
// starts nothing; only the final button sends the request.
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Checkbox, CheckboxGroup, Input, Label, SearchField, Text } from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import {
  ApiError,
  type ChoosableProfile,
  type Preparation,
  preparationQuery,
  REGULATORY_PROFILES,
  type ScanMode,
  type Site,
  siteQuery,
  sitesQuery,
  startCheck,
  startScan,
  updateSite,
} from '../api';
import { Button, Choice, Field, Message, WebsiteLink } from '../ui';
import { pathOf, publicPage } from '../util';
import { GuideLink } from './manual-parts';

const sectionTitle = 'text-lg font-bold';

/** Target standard that follows each choosable profile (same mapping as the API). */
const STANDARD_OF: Record<ChoosableProfile, string> = {
  eaa: 'EN301549-4.1.1',
  'sector-publico': 'EN301549-3.2.1',
  voluntario: 'WCAG22-AA',
};

type PagePick = 'auto' | 'manual';
type Step = 'profile' | 'mode' | 'pages' | 'summary';

/** Same host ignoring www, for the URLs the person adds before the server checks them. */
function sameHost(url: string, siteUrl: string): boolean {
  try {
    const strip = (host: string) => host.toLowerCase().replace(/^www\./, '');
    return strip(new URL(url).hostname) === strip(new URL(siteUrl).hostname);
  } catch {
    return false;
  }
}

/** Turns a typed address into an absolute URL of the project, or null when it cannot be. */
function resolveTypedUrl(raw: string, siteUrl: string): string | null {
  const value = raw.trim();
  if (!value) return null;
  try {
    const absolute = value.startsWith('/')
      ? new URL(value, siteUrl).href
      : /^[a-z][a-z\d+.-]*:\/\//i.test(value)
        ? value
        : `https://${value}`;
    const url = new URL(absolute);
    if (!['http:', 'https:'].includes(url.protocol) || url.username || url.password) return null;
    url.hash = '';
    if (!sameHost(url.href, siteUrl)) return null;
    return url.href;
  } catch {
    return null;
  }
}

function stepsOf(needsProfile: boolean): Step[] {
  return needsProfile ? ['profile', 'mode', 'pages', 'summary'] : ['mode', 'pages', 'summary'];
}

export function PrepareAnalysis({
  site,
  check = [],
  onVerify,
  onClose,
  onStarted,
}: {
  site: Site;
  /** The elements whose fix is being checked: the summary then offers to analyse just their pages. */
  check?: string[];
  /** Leads to the ownership verification; absent when the person's role cannot verify. */
  onVerify?: () => void;
  onClose: () => void;
  /** The system accepted the request: the progress is shown next. */
  onStarted: () => Promise<void> | void;
}) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const prep = useQuery(preparationQuery(site.id, check));
  // What was chosen stays while the request is retried: nothing has to be filled in again.
  const [mode, setMode] = useState<ScanMode | 'comprobacion'>(check.length > 0 ? 'comprobacion' : 'pagina');
  const [pagePick, setPagePick] = useState<PagePick>('auto');
  const [selected, setSelected] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [extraUrl, setExtraUrl] = useState('');
  const [addError, setAddError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // First analysis only: the website has no law yet (never deduced from the domain). Later changes go in settings.
  const needsProfile = site.regulatoryProfile === 'sin-definir';
  const [profile, setProfile] = useState<ChoosableProfile | null>(null);
  const steps = stepsOf(needsProfile);
  const [step, setStep] = useState<Step>(steps[0] ?? 'mode');
  // A second press (Enter twice, a double click) must not send a second request: the state only changes
  // after the next render, this does at once.
  const sending = useRef(false);
  const stepHeading = useRef<HTMLHeadingElement>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: refocus the step title whenever the step changes
  useEffect(() => {
    if (prep.isSuccess) stepHeading.current?.focus();
  }, [step, prep.isSuccess]);

  const data: Preparation | undefined = prep.data;
  const checking = mode === 'comprobacion';
  const scope = data?.check ?? null;
  const checkable = Boolean(scope && scope.pages.length > 0 && !scope.tooMany);
  const chosen = checking ? undefined : data?.modes.find((candidate) => candidate.id === mode);
  const maxPages = chosen?.maxPages ?? 0;
  const usage = data?.usage;
  const canPick = Boolean(data?.canSelectPages) && !checking;
  const picking = canPick && pagePick === 'manual';
  const planned = checking ? (scope?.planned ?? 0) : picking ? selected.length : (chosen?.maxPages ?? 0);
  const remaining = usage?.remaining ?? null;
  const enough = remaining === null || planned <= remaining;
  const lacksOwnership = Boolean(chosen?.needsVerification && !data?.verified);
  const hasSelection = !picking || selected.length > 0;
  const canStart = Boolean(
    data &&
      (!needsProfile || profile) &&
      (checking ? checkable : chosen) &&
      enough &&
      !lacksOwnership &&
      !data.active &&
      hasSelection &&
      planned > 0,
  );
  // The pages are available again on the first day of the month, UTC: the date is said in that zone.
  const renews = usage
    ? new Intl.DateTimeFormat(i18n.language, { day: 'numeric', month: 'long', timeZone: 'UTC' }).format(
        new Date(usage.renewsAt),
      )
    : '';
  // Said at the start, before the options, when nothing is left: the rest of the page does not repeat it.
  const nothingLeft = remaining !== null && remaining <= 0;
  const targetStandard = profile ? STANDARD_OF[profile] : site.targetStandard;
  const standardLabel = t(`sites.standards.${targetStandard}`, { defaultValue: targetStandard });
  const wcag = /WCAG \d\.\d AA/.exec(standardLabel)?.[0] ?? 'WCAG 2.2 AA';
  const profileOptions = REGULATORY_PROFILES.map((value) => ({
    value,
    label: t(`sites.profiles.${value}`),
    hint: t(`sites.profileHints.${value}`),
  }));

  const stepIndex = steps.indexOf(step);
  const isFirst = stepIndex <= 0;
  const isLast = stepIndex === steps.length - 1;

  const stepReady =
    step === 'profile'
      ? Boolean(profile)
      : step === 'mode'
        ? Boolean(checking ? checkable : chosen && !lacksOwnership)
        : step === 'pages'
          ? Boolean(checking ? checkable : hasSelection && planned > 0)
          : canStart;

  const knownPages = useMemo(() => {
    const list = data?.pages ?? [];
    const home = data?.url;
    if (!home || list.some((page) => page.url === home)) return list;
    return [{ url: home, title: null }, ...list];
  }, [data?.pages, data?.url]);

  const filteredPages = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return knownPages;
    return knownPages.filter(
      (page) => page.url.toLowerCase().includes(query) || (page.title ?? '').toLowerCase().includes(query),
    );
  }, [knownPages, search]);

  // When the mode's limit shrinks, keep only as many addresses as it allows (home first).
  useEffect(() => {
    if (!picking || maxPages <= 0) return;
    setSelected((current) => {
      if (current.length <= maxPages) return current;
      const home = data?.url;
      const preferred =
        home && current.includes(home) ? [home, ...current.filter((url) => url !== home)] : current;
      return preferred.slice(0, maxPages);
    });
  }, [picking, maxPages, data?.url]);

  const singlePage = maxPages === 1;

  function changeMode(next: ScanMode | 'comprobacion') {
    setMode(next);
    setError(null);
    if (next === 'pagina' && data?.url && pagePick === 'manual') setSelected([data.url]);
  }

  function goNext() {
    if (!stepReady || isLast) return;
    const next = steps[stepIndex + 1];
    if (next) setStep(next);
  }

  function goBack() {
    if (isFirst) return;
    const prev = steps[stepIndex - 1];
    if (prev) setStep(prev);
  }

  /** One page: replace what was chosen. Several: append until the mode's limit. */
  function choosePages(next: string[]) {
    if (singlePage) {
      if (next.length === 0) setSelected([]);
      else if (next.length === 1) setSelected(next);
      else {
        // A second box was checked while one was on: keep only the newly chosen address.
        const added = next.find((url) => !selected.includes(url));
        setSelected(added ? [added] : next.slice(-1));
      }
      return;
    }
    if (next.length > maxPages) return;
    setSelected(next);
  }

  function addTypedUrl() {
    if (!data || !picking) return;
    const url = resolveTypedUrl(extraUrl, data.url);
    if (!url) {
      setAddError(t('scans.prepare.pages.addInvalid'));
      return;
    }
    if (selected.includes(url)) {
      setAddError(t('scans.prepare.pages.addDuplicate'));
      return;
    }
    // One page: the typed address replaces the current choice; several: refuse past the limit.
    if (singlePage) {
      setSelected([url]);
      setExtraUrl('');
      setAddError(null);
      return;
    }
    if (selected.length >= maxPages) {
      setAddError(t('scans.prepare.pages.addLimit', { count: maxPages }));
      return;
    }
    setSelected((current) => [...current, url]);
    setExtraUrl('');
    setAddError(null);
  }

  async function start() {
    if (sending.current || !canStart) return;
    if (needsProfile && !profile) return;
    sending.current = true;
    setBusy(true);
    setError(null);
    try {
      // First time only: save the profile before the scan so the engine uses the chosen standard.
      if (needsProfile && profile) {
        const updated = await updateSite(site.id, { regulatoryProfile: profile });
        queryClient.setQueryData(siteQuery(site.id).queryKey, updated);
        await queryClient.invalidateQueries({ queryKey: sitesQuery.queryKey, exact: true });
      }
      if (mode === 'comprobacion') await startCheck(site.id, check);
      else await startScan(site.id, mode, picking ? selected : undefined);
      await onStarted();
    } catch (failure) {
      const code = failure instanceof ApiError ? failure.code : null;
      if (code === 'saldo') void prep.refetch();
      setError(
        code &&
          [
            'saldo',
            'en-curso',
            'verificacion',
            'limite',
            'permiso',
            'plan',
            'urls',
            'nada-que-comprobar',
            'demasiadas-paginas',
            'limite-paginas',
          ].includes(code)
          ? t(`scans.errors.${code}`)
          : t('scans.prepare.startFailed'),
      );
    } finally {
      sending.current = false;
      setBusy(false);
    }
  }

  const checkOption =
    check.length > 0 && scope
      ? [
          {
            value: 'comprobacion' as const,
            label: t('scans.prepare.check.mode'),
            hint:
              scope.pages.length > 0
                ? t('scans.prepare.check.modeHint', { count: scope.pages.length })
                : t('scans.prepare.check.nothing'),
            disabled: !checkable,
            action: undefined,
          },
        ]
      : [];
  const options = [
    ...checkOption,
    ...(data?.modes ?? []).map((candidate) => {
      const needs = candidate.needsVerification && !data?.verified;
      const overQuota = remaining !== null && remaining > 0 && candidate.maxPages > remaining;
      const ownershipAction = needs ? (
        onVerify ? (
          <Link
            key={`${candidate.id}-verify`}
            to="/sites/$siteId/verificacion"
            params={{ siteId: site.id }}
            className="text-sm font-bold text-brand underline hover:no-underline"
          >
            {t('scans.prepare.verifyAction')}
          </Link>
        ) : (
          <p key={`${candidate.id}-readonly`} className="text-sm text-ink-muted">
            {t('site.verification.readOnly')}
          </p>
        )
      ) : undefined;
      const quotaAction = overQuota ? (
        <div key={`${candidate.id}-quota`} className="flex flex-col items-start gap-2">
          <p className="text-sm font-bold text-error">
            {t('scans.prepare.optionOverQuota', {
              max: candidate.maxPages,
              remaining,
            })}
          </p>
          <div className="flex flex-wrap items-center gap-4">
            {candidate.id !== 'pagina' && (
              <Button variant="secondary" onPress={() => changeMode('pagina')}>
                {t('scans.prepare.useOnePage')}
              </Button>
            )}
            <WebsiteLink href={publicPage(i18n.language, 'pricing')}>
              {t('scans.prepare.seePlans')}
            </WebsiteLink>
          </div>
        </div>
      ) : undefined;
      return {
        value: candidate.id,
        label: t(`scans.prepare.modes.${candidate.id}.label`),
        hint: [
          t(`scans.prepare.modes.${candidate.id}.hint`, { count: candidate.maxPages }),
          needs ? t('scans.prepare.needsOwnership') : '',
        ]
          .filter(Boolean)
          .join(' '),
        disabled: needs,
        action: ownershipAction ?? quotaAction,
      };
    }),
  ];

  const startLabel = busy
    ? t('scans.prepare.starting')
    : picking
      ? t('scans.prepare.startExact', { count: planned })
      : t('scans.prepare.startAuto', { count: planned });

  const stepTitleId =
    step === 'profile'
      ? 'preparar-perfil'
      : step === 'mode'
        ? 'preparar-tipo'
        : step === 'pages'
          ? checking
            ? 'alcance-comprobacion'
            : 'paginas-analisis'
          : 'preparar-resumen';

  const stepTitle =
    step === 'profile'
      ? t('scans.prepare.profile')
      : step === 'mode'
        ? t('scans.prepare.scopeTitle')
        : step === 'pages'
          ? checking
            ? t('scans.prepare.check.title')
            : t('scans.prepare.pages.title')
          : t('scans.prepare.summaryTitle');

  function navButtons() {
    return (
      <div className={isLast ? 'prepare-actions' : 'prepare-nav'}>
        {!isFirst && (
          <Button variant="secondary" onPress={goBack} isDisabled={busy}>
            {t('scans.prepare.back')}
          </Button>
        )}
        {!isLast && (
          <Button onPress={goNext} isDisabled={!stepReady || busy}>
            {t('scans.prepare.next')}
          </Button>
        )}
        {isLast && (
          <Button onPress={() => void start()} isPending={busy} isDisabled={!canStart && !busy}>
            {startLabel}
          </Button>
        )}
        <Button variant="secondary" onPress={onClose} isDisabled={busy}>
          {t('scans.prepare.cancel')}
        </Button>
      </div>
    );
  }

  return (
    <section aria-label={t('scans.prepare.title')} className="prepare-panel flex flex-col gap-6">
      {prep.isPending ? (
        <p role="status">{t('app.loading')}</p>
      ) : prep.isError || !data ? (
        <div className="flex flex-col items-start gap-3">
          <Message tone="error">{t('scans.prepare.loadFailed')}</Message>
          <div className="flex flex-wrap items-center gap-4">
            <Button onPress={() => void prep.refetch()}>{t('app.retry')}</Button>
            <Button variant="secondary" onPress={onClose}>
              {t('scans.prepare.cancel')}
            </Button>
          </div>
        </div>
      ) : (
        <>
          {nothingLeft && (
            <div
              role="alert"
              className="flex flex-col items-start gap-2 rounded-md border border-error bg-paper-alt p-4"
            >
              <p className="font-bold">{t('scans.prepare.noPages.title')}</p>
              <p>{t('scans.prepare.noPages.text', { date: renews })}</p>
              <WebsiteLink href={publicPage(i18n.language, 'pricing')}>
                {t('scans.prepare.seePlans')}
              </WebsiteLink>
            </div>
          )}

          <div className="flex flex-col gap-4">
            <h4
              id={stepTitleId}
              ref={stepHeading}
              tabIndex={-1}
              className={`${sectionTitle} focus:outline-none`}
            >
              {stepTitle}
            </h4>

            {step === 'profile' && (
              <Choice
                name="regulatoryProfile"
                label={t('scans.prepare.profile')}
                hideLabel
                description={t('scans.prepare.profileUnset')}
                options={profileOptions}
                value={profile}
                onChange={(value) => {
                  setProfile(value);
                  setError(null);
                }}
              />
            )}

            {step === 'mode' && (
              <Choice
                name="mode"
                label={t('scans.prepare.scopeTitle')}
                hideLabel
                options={options}
                value={mode}
                onChange={changeMode}
              />
            )}

            {step === 'pages' && !checking && (
              <section aria-labelledby="paginas-analisis" className="flex flex-col gap-3">
                <Choice
                  name="page-pick"
                  label={t('scans.prepare.pages.how')}
                  options={[
                    {
                      value: 'auto',
                      label: t('scans.prepare.pages.auto.label'),
                      hint: t('scans.prepare.pages.auto.hint', { count: maxPages }),
                    },
                    {
                      value: 'manual',
                      label: t('scans.prepare.pages.manual.label'),
                      hint: canPick
                        ? t('scans.prepare.pages.manual.hint', { count: maxPages })
                        : t('scans.prepare.pages.manual.paidOnly'),
                      disabled: !canPick,
                      action: !canPick ? (
                        <WebsiteLink href={publicPage(i18n.language, 'pricing')}>
                          {t('scans.prepare.seePlans')}
                        </WebsiteLink>
                      ) : undefined,
                    },
                  ]}
                  value={pagePick}
                  onChange={(value) => {
                    setPagePick(value);
                    setAddError(null);
                    setError(null);
                    if (value === 'manual' && data.url) setSelected([data.url]);
                  }}
                />

                {picking && (
                  <div className="flex flex-col gap-4 rounded-lg border border-line bg-paper p-4">
                    <SearchField value={search} onChange={setSearch} className="flex flex-col gap-2">
                      <Label className="font-bold">{t('scans.prepare.pages.search')}</Label>
                      <Input
                        className="min-h-11 rounded-md border border-field-border bg-paper px-3 py-2 text-ink"
                        placeholder={t('scans.prepare.pages.searchPlaceholder')}
                      />
                    </SearchField>

                    <CheckboxGroup
                      aria-label={t('scans.prepare.pages.listLabel')}
                      value={selected}
                      onChange={(next) => choosePages(next as string[])}
                      className="flex flex-col gap-2"
                    >
                      <p className="text-sm text-ink-muted">
                        {t('scans.prepare.pages.chosen', { count: selected.length, max: maxPages })}
                      </p>
                      {filteredPages.length === 0 ? (
                        <p className="text-sm text-ink-muted">{t('scans.prepare.pages.empty')}</p>
                      ) : (
                        <ul className="flex max-h-64 flex-col gap-1 overflow-y-auto">
                          {filteredPages.map((page) => {
                            // One page: never disable the others — choosing another replaces the current.
                            const atLimit =
                              !singlePage && selected.length >= maxPages && !selected.includes(page.url);
                            return (
                              <li key={page.url}>
                                <Checkbox
                                  value={page.url}
                                  isDisabled={atLimit}
                                  className="group flex cursor-pointer items-start gap-3 rounded-md p-2 outline-none data-[disabled]:cursor-not-allowed data-[focus-visible]:outline-3 data-[focus-visible]:outline-offset-2 data-[focus-visible]:outline-brand"
                                >
                                  <span
                                    aria-hidden="true"
                                    className={
                                      singlePage
                                        ? 'mt-0.5 size-5 shrink-0 rounded-full border border-field-border group-data-[selected]:border-[6px] group-data-[selected]:border-brand'
                                        : 'mt-0.5 flex size-5 shrink-0 items-center justify-center rounded border border-field-border group-data-[selected]:border-brand group-data-[selected]:bg-brand group-data-[disabled]:opacity-50'
                                    }
                                  >
                                    {!singlePage && (
                                      <span className="hidden size-2.5 bg-paper group-data-[selected]:block" />
                                    )}
                                  </span>
                                  <span className="flex min-w-0 flex-col">
                                    {page.title && <span className="font-bold">{page.title}</span>}
                                    <span className="wrap-anywhere text-sm text-ink-muted">
                                      {pathOf(page.url)}
                                    </span>
                                  </span>
                                </Checkbox>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </CheckboxGroup>

                    <div className="flex flex-col gap-2 border-t border-line pt-4">
                      <Field
                        label={t('scans.prepare.pages.addLabel')}
                        description={t('scans.prepare.pages.addHint')}
                        value={extraUrl}
                        onChange={(value) => {
                          setExtraUrl(value);
                          setAddError(null);
                        }}
                        placeholder={t('scans.prepare.pages.addPlaceholder')}
                        error={addError}
                      />
                      <div>
                        <Button variant="secondary" onPress={addTypedUrl}>
                          {t('scans.prepare.pages.addAction')}
                        </Button>
                      </div>
                    </div>

                    {selected.length > 0 && (
                      <div className="flex flex-col gap-2 border-t border-line pt-4">
                        <h5 className="font-bold">{t('scans.prepare.pages.summaryTitle')}</h5>
                        <p>{t('scans.prepare.pages.summaryCount', { count: selected.length })}</p>
                        <ul className="check-pages">
                          {selected.map((url) => (
                            <li key={url} className="wrap-anywhere">
                              {url}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {!picking && (
                  <Text className="text-sm text-ink-muted">
                    {mode === 'pagina'
                      ? t('scans.prepare.pages.autoOne', { url: data.url })
                      : t('scans.prepare.pages.autoMulti', { count: maxPages })}
                  </Text>
                )}
              </section>
            )}

            {step === 'pages' && checking && scope && (
              <section aria-labelledby="alcance-comprobacion" className="flex flex-col gap-2">
                <p>{t('scans.prepare.check.intro')}</p>
                {scope.tooMany ? (
                  <Message tone="error">
                    {t('scans.prepare.check.tooMany', { count: scope.pages.length, max: scope.maxPages })}
                  </Message>
                ) : scope.pages.length === 0 ? (
                  <Message tone="error">{t('scans.prepare.check.nothing')}</Message>
                ) : (
                  <>
                    <p>
                      {t('scans.prepare.check.elements', { count: scope.elements.length })} ·{' '}
                      {t('scans.prepare.check.scope', { count: scope.pages.length })}
                    </p>
                    <h5 className="font-bold">{t('scans.prepare.check.pagesTitle')}</h5>
                    <ul className="check-pages">
                      {scope.pages.map((page) => (
                        <li key={page.url}>
                          <span className="wrap-anywhere font-bold">{pathOf(page.url)}</span>
                          <span className="text-ink-muted">
                            {' '}
                            · {t('scans.prepare.check.elements', { count: page.elements })} ·{' '}
                            {t('scans.prepare.check.devices', {
                              list: page.devices.map((device) => t(`scan.devices.${device}`)).join(', '),
                            })}
                          </span>
                        </li>
                      ))}
                    </ul>
                    <h5 className="font-bold">{t('scans.prepare.check.conditionsTitle')}</h5>
                    <p>{t('scans.prepare.check.conditions')}</p>
                  </>
                )}
                {scope.skipped > 0 && (
                  <p className="text-sm text-ink-muted">
                    {t('scans.prepare.check.skipped', { count: scope.skipped })}
                  </p>
                )}
                <p className="text-sm text-ink-muted">{t('scans.prepare.check.result')}</p>
                {scope.elements.some((item) => item.manual.needsHuman && item.manual.guides.length > 0) && (
                  <div className="follow-verify">
                    <h5 className="font-bold">{t('manual.prepare.title')}</h5>
                    <p className="text-sm">{t('manual.prepare.intro')}</p>
                    <ul className="check-pages">
                      {scope.elements
                        .filter((item) => item.manual.needsHuman && item.manual.guides.length > 0)
                        .flatMap((item) =>
                          item.manual.guides.map((id) => (
                            <li key={`${item.id}-${id}`}>
                              <GuideLink
                                siteId={site.id}
                                criterion={id}
                                search={{ occurrence: item.id, p: item.page.key }}
                              >
                                {t('manual.prepare.repeat', { id, element: item.element.title })}
                              </GuideLink>
                            </li>
                          )),
                        )}
                    </ul>
                  </div>
                )}
              </section>
            )}

            {step === 'summary' && (
              <>
                <div className="prepare-quota rounded-lg border border-line bg-paper p-4 flex flex-col gap-4">
                  <div>
                    <h5 className="font-bold">{t('scans.prepare.analysisLimitTitle')}</h5>
                    <p>
                      {picking
                        ? t('scans.prepare.analysisLimitExact', { count: planned })
                        : t('scans.prepare.analysisLimit', { count: planned })}
                    </p>
                  </div>
                  <div>
                    <h5 className="font-bold">{t('scans.prepare.monthlyQuotaTitle')}</h5>
                    {usage && usage.limit !== null && remaining !== null ? (
                      <>
                        <p>{t('scans.prepare.monthlyUsedOf', { used: usage.used, limit: usage.limit })}</p>
                        <p>{t('scans.prepare.monthlyAvailable', { count: remaining })}</p>
                      </>
                    ) : (
                      <>
                        <p>{t('scans.prepare.monthlyUnlimited')}</p>
                        <p>{t('scans.prepare.monthlyAnalysed', { count: usage?.used ?? 0 })}</p>
                      </>
                    )}
                    <p className="mt-2 text-sm">
                      <WebsiteLink href={publicPage(i18n.language, 'pages')}>
                        {t('scans.prepare.pagesHelp')}
                      </WebsiteLink>
                    </p>
                  </div>
                  {!enough && !nothingLeft && (
                    <div
                      role="alert"
                      className="flex flex-col items-start gap-3 rounded-md border border-error bg-paper-alt p-3"
                    >
                      <p className="font-bold">
                        {t('scans.prepare.insufficient', { planned, remaining, date: renews })}
                      </p>
                      <div className="flex flex-wrap items-center gap-4">
                        {mode !== 'pagina' && (
                          <Button
                            variant="secondary"
                            onPress={() => {
                              changeMode('pagina');
                              setStep('mode');
                            }}
                          >
                            {t('scans.prepare.useOnePage')}
                          </Button>
                        )}
                        <WebsiteLink href={publicPage(i18n.language, 'pricing')}>
                          {t('scans.prepare.seePlans')}
                        </WebsiteLink>
                      </div>
                    </div>
                  )}
                </div>

                <p className="text-ink-muted">
                  {t('scans.prepare.checks', { standard: wcag })}{' '}
                  <WebsiteLink href={publicPage(i18n.language, 'methodology')}>
                    {t('scans.prepare.methodology')}
                  </WebsiteLink>
                </p>
              </>
            )}
          </div>

          {data.active && <Message tone="error">{t('scans.errors.en-curso')}</Message>}
          {error && <Message tone="error">{error}</Message>}
          {navButtons()}
        </>
      )}
    </section>
  );
}
